import { useMemo } from 'react';
import { authService } from '../services/auth';
import { 
  hasPermission, 
  getUserPermissions, 
  canAccessRoute, 
  canSeeNavigationItem,
  UserRole,
  Permission 
} from '../utils/permissions';

export const usePermissions = () => {
  const user = authService.getUser();
  const userRole = user?.role;

  const permissions = useMemo(() => {
    if (!userRole) return [];
    return getUserPermissions(userRole);
  }, [userRole]);

  const checkPermission = (permission) => {
    return hasPermission(userRole, permission);
  };

  const canAccess = (pathname) => {
    return canAccessRoute(userRole, pathname);
  };

  const canSeeNavItem = (itemName) => {
    return canSeeNavigationItem(userRole, itemName);
  };

  const isAdmin = () => {
    return userRole === UserRole.ADMIN;
  };

  const isManager = () => {
    return userRole === UserRole.MANAGER || isAdmin();
  };

  const isAccountant = () => {
    return userRole === UserRole.ACCOUNTANT || isAdmin();
  };

  const isTeacher = () => {
    return userRole === UserRole.TEACHER || isManager();
  };

  // Common permission checks
  const canManageStudents = () => {
    return checkPermission(Permission.CREATE_STUDENT) || 
           checkPermission(Permission.UPDATE_STUDENT) || 
           checkPermission(Permission.DELETE_STUDENT);
  };

  const canManageBatches = () => {
    return checkPermission(Permission.CREATE_BATCH) || 
           checkPermission(Permission.UPDATE_BATCH) || 
           checkPermission(Permission.DELETE_BATCH);
  };

  const canManageEnrollments = () => {
    return checkPermission(Permission.CREATE_ENROLLMENT) || 
           checkPermission(Permission.UPDATE_ENROLLMENT) || 
           checkPermission(Permission.DELETE_ENROLLMENT);
  };

  const canManageFees = () => {
    return checkPermission(Permission.CREATE_FEE_STRUCTURE) || 
           checkPermission(Permission.UPDATE_FEE_STRUCTURE) || 
           checkPermission(Permission.DELETE_FEE_STRUCTURE);
  };

  const canManagePayments = () => {
    return checkPermission(Permission.MANAGE_PAYMENTS);
  };

  const canViewPayments = () => {
    return checkPermission(Permission.VIEW_PAYMENTS);
  };

  const canManageUsers = () => {
    return checkPermission(Permission.MANAGE_USERS);
  };

  return {
    user,
    userRole,
    permissions,
    checkPermission,
    canAccess,
    canSeeNavItem,
    isAdmin,
    isManager,
    isAccountant,
    isTeacher,
    canManageStudents,
    canManageBatches,
    canManageEnrollments,
    canManageFees,
    canManagePayments,
    canViewPayments,
    canManageUsers,
    // Export constants for convenience
    UserRole,
    Permission
  };
}; 